import { CONFIG } from '../config';
import type { Boss } from '../entities/Boss';
import type { Bullet } from '../entities/Bullet';
import type { BossType } from '../types';
import { RotatingTurret } from '../entities/bosses/RotatingTurret';
import { Chaser } from '../entities/bosses/Chaser';
import { Splitter } from '../entities/bosses/Splitter';
import { BossDeathEffect } from '../entities/BossDeathEffect';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

/**
 * 보스 스폰 및 관리
 */
export class BossManager {
    private bosses: Boss[] = [];
    private bossCount: number = 0;
    private spawnTimer: number = 0;
    private canvasWidth: number;
    private canvasHeight: number;
    private isLandscape: boolean = false;
    private testMode: boolean = false;
    private onBossSpawn: (() => void) | null = null;
    private onBossDefeat: ((bossType: BossType) => void) | null = null;
    private deathEffects: BossDeathEffect[] = [];
    private renderer: RendererLike | null = null;

    constructor(canvasWidth: number, canvasHeight: number) {
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
    }

    /**
     * 렌더러 설정 (동적 캔버스 크기 지원)
     */
    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    /**
     * 현재 캔버스 크기 및 방향 가져오기
     */
    private getCanvasSize(): { width: number; height: number; isLandscape: boolean } {
        if (this.renderer) {
            return {
                width: this.renderer.getCanvasWidth(),
                height: this.renderer.getCanvasHeight(),
                isLandscape: this.renderer.isLandscape
            };
        }
        return {
            width: this.canvasWidth,
            height: this.canvasHeight,
            isLandscape: this.isLandscape
        };
    }

    /**
     * 가로/세로 모드 설정
     */
    setLandscape(isLandscape: boolean): void {
        this.isLandscape = isLandscape;
    }

    /**
     * 테스트 모드 설정 (20초마다 보스 연속 등장)
     */
    setTestMode(enabled: boolean): void {
        this.testMode = enabled;
    }

    /**
     * 보스 스폰 콜백 설정
     */
    setOnBossSpawn(callback: () => void): void {
        this.onBossSpawn = callback;
    }

    /**
     * 보스 처치 콜백 설정
     */
    setOnBossDefeat(callback: (bossType: BossType) => void): void {
        this.onBossDefeat = callback;
    }

    /**
     * 업데이트
     */
    update(deltaTime: number, gameTime: number, playerX: number, playerY: number): void {
        // 보스 스폰 타이머
        this.spawnTimer += deltaTime;

        // 보스 스폰 조건
        const spawnInterval = this.testMode ? 3 : CONFIG.BOSS.SPAWN_INTERVAL;
        const canSpawn = this.bosses.length === 0; // 테스트 모드에서도 보스가 없을 때만 스폰

        if (this.spawnTimer >= spawnInterval && canSpawn) {
            this.spawnTimer = 0;
            this.spawnRandomBoss();
        }

        // 시간 경과에 따른 데미지 계산 (15초 동안 체력이 0이 되도록)
        const damagePerSecond = 100 / CONFIG.BOSS.DEFEAT_TIME;
        const damageThisFrame = damagePerSecond * deltaTime;

        // 모든 보스 업데이트
        for (let i = this.bosses.length - 1; i >= 0; i--) {
            const boss = this.bosses[i];
            boss.update(deltaTime, playerX, playerY);

            // 시간 경과에 따른 체력 감소
            boss.takeDamage(damageThisFrame);

            // 죽은 보스 처리
            if (!boss.isAlive) {
                // 사망 이펙트 생성
                const deathEffect = new BossDeathEffect(boss.x, boss.y, boss.radius);
                this.deathEffects.push(deathEffect);

                // Splitter 특수 처리: 분열
                if (boss instanceof Splitter && boss.canSplit()) {
                    const newSplitters = boss.split();
                    if (newSplitters) {
                        this.bosses.push(...newSplitters);
                    }
                }

                this.bosses.splice(i, 1);

                // 모든 보스가 처치되었을 때 콜백 및 타이머 리셋
                if (this.bosses.length === 0) {
                    // 타이머 리셋 - 다음 보스까지 대기
                    this.spawnTimer = 0;

                    if (this.onBossDefeat) {
                        this.onBossDefeat(boss.bossType);
                    }
                }
            }
        }

        // 사망 이펙트 업데이트
        for (let i = this.deathEffects.length - 1; i >= 0; i--) {
            const effect = this.deathEffects[i];
            effect.update(deltaTime);

            if (!effect.getIsActive()) {
                this.deathEffects.splice(i, 1);
            }
        }
    }

    /**
     * 랜덤 보스 스폰
     */
    private spawnRandomBoss(): void {
        const bossTypes: BossType[] = ['rotatingTurret', 'chaser', 'splitter'];
        const randomType = bossTypes[Math.floor(Math.random() * bossTypes.length)];

        this.spawnBoss(randomType);
    }

    /**
     * 특정 보스 스폰
     */
    spawnBoss(type: BossType): void {
        const { width, height, isLandscape } = this.getCanvasSize();
        let boss: Boss;

        switch (type) {
            case 'rotatingTurret':
                boss = new RotatingTurret(width, height, isLandscape);
                break;
            case 'chaser':
                boss = new Chaser(width, height, isLandscape);
                break;
            case 'splitter':
                boss = new Splitter(width, height, undefined, undefined, 0, isLandscape);
                break;
            default:
                boss = new RotatingTurret(width, height, isLandscape);
        }

        this.bosses.push(boss);
        this.bossCount++;

        if (this.onBossSpawn) {
            this.onBossSpawn();
        }
    }

    /**
     * 모든 보스의 총알 수집
     */
    collectBullets(): Bullet[] {
        const allBullets: Bullet[] = [];

        for (const boss of this.bosses) {
            const bullets = boss.getBullets();
            allBullets.push(...bullets);
        }

        return allBullets;
    }

    /**
     * 보스에게 데미지
     */
    damageNearestBoss(x: number, y: number, damage: number = 1): boolean {
        if (this.bosses.length === 0) return false;

        // 가장 가까운 보스 찾기
        let nearestBoss: Boss | null = null;
        let nearestDistance = Infinity;

        for (const boss of this.bosses) {
            const dx = boss.x - x;
            const dy = boss.y - y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestBoss = boss;
            }
        }

        if (nearestBoss) {
            return nearestBoss.takeDamage(damage);
        }

        return false;
    }

    /**
     * 특정 위치와 보스 충돌 체크
     */
    checkCollision(x: number, y: number, radius: number): Boss | null {
        for (const boss of this.bosses) {
            const bounds = boss.getBounds();
            const dx = x - bounds.x;
            const dy = y - bounds.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < radius + bounds.radius) {
                return boss;
            }
        }
        return null;
    }

    /**
     * 렌더링
     */
    render(ctx: CanvasRenderingContext2D): void {
        for (const boss of this.bosses) {
            boss.render(ctx);
        }

        // 사망 이펙트 렌더링
        for (const effect of this.deathEffects) {
            effect.render(ctx);
        }
    }

    /**
     * 현재 활성 보스들 반환
     */
    getBosses(): Boss[] {
        return this.bosses;
    }

    /**
     * 메인 보스 반환 (UI용 - 첫 번째 보스)
     */
    getMainBoss(): Boss | null {
        return this.bosses.length > 0 ? this.bosses[0] : null;
    }

    /**
     * 보스 활성 여부
     */
    isBossActive(): boolean {
        return this.bosses.length > 0;
    }

    /**
     * 처치한 보스 수
     */
    getBossCount(): number {
        return this.bossCount;
    }

    /**
     * 다음 보스까지 남은 시간
     */
    getTimeToNextBoss(): number {
        return Math.max(0, CONFIG.BOSS.SPAWN_INTERVAL - this.spawnTimer);
    }

    /**
     * 강제 보스 스폰 (디버그용)
     */
    forceSpawnBoss(type?: BossType): void {
        if (type) {
            this.spawnBoss(type);
        } else {
            this.spawnRandomBoss();
        }
    }

    /**
     * 사망 이펙트 배열 반환
     */
    getDeathEffects(): BossDeathEffect[] {
        return this.deathEffects;
    }

    /**
     * 리셋
     */
    reset(): void {
        this.bosses = [];
        this.bossCount = 0;
        this.spawnTimer = 0;
        this.deathEffects = [];
    }

    /**
     * 화면 방향 전환 시 보스 위치 변환
     */
    transformPositionsOnOrientationChange(wasLandscape: boolean, isLandscape: boolean): void {
        const oldWidth = wasLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const oldHeight = wasLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const newWidth = isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const newHeight = isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;

        for (const boss of this.bosses) {
            // 정규화된 좌표 (0-1 범위)
            const normalizedX = boss.x / oldWidth;
            const normalizedY = boss.y / oldHeight;

            if (wasLandscape && !isLandscape) {
                // Landscape → Portrait: 90도 시계방향 회전
                boss.x = normalizedY * newWidth;
                boss.y = (1 - normalizedX) * newHeight;
            } else {
                // Portrait → Landscape: 90도 반시계방향 회전
                boss.x = (1 - normalizedY) * newWidth;
                boss.y = normalizedX * newHeight;
            }

            // 보스 내부 캔버스 크기 업데이트
            boss.canvasWidth = newWidth;
            boss.canvasHeight = newHeight;

            // 화면 안에 있도록 클램프
            boss.x = Math.max(boss.radius, Math.min(newWidth - boss.radius, boss.x));
            boss.y = Math.max(boss.radius, Math.min(newHeight - boss.radius, boss.y));
        }

        // 사망 이펙트도 변환
        for (const effect of this.deathEffects) {
            const normalizedX = effect.x / oldWidth;
            const normalizedY = effect.y / oldHeight;

            if (wasLandscape && !isLandscape) {
                effect.x = normalizedY * newWidth;
                effect.y = (1 - normalizedX) * newHeight;
            } else {
                effect.x = (1 - normalizedY) * newWidth;
                effect.y = normalizedX * newHeight;
            }
        }
    }
}
